import React from "react";
import { Link } from "react-router-dom";
import { Gem, Truck, Package, CalendarCheck, ShieldCheck, Leaf, Heart, Sparkles, RefreshCcw } from "lucide-react";
import { waLink } from "@/components/WhatsApp";
import { useSite } from "@/context/SiteContext";

const sid = (s: string) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
const tel = (p: string) => p && `tel:${p.replace(/[^+\d]/g, "")}`;

const RingIcon = ({ size = 24, strokeWidth = 1, className = "" }: any) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <circle cx="12" cy="15.2" r="6.3" /><circle cx="12" cy="15.2" r="4.9" /><path d="M9.6 5.6 10.9 3.6h2.2l1.3 2-2.4 3.3z" /><path d="M9.6 5.6h4.8" />
  </svg>
);

export const ROW_ICONS: Record<string, any> = { gem: Gem, ring: RingIcon, shipping: Truck, package: Package, calendar: CalendarCheck, returns: RefreshCcw, shield: ShieldCheck, leaf: Leaf, heart: Heart, sparkle: Sparkles };

const Go = ({ to, children, testId }: any) => {
  const cls = "underline underline-offset-4 hover:text-[var(--c-primary)] transition-colors";
  if (/^(https?:|mailto:|tel:)/i.test(to)) return <a href={to} target={/^https?:/i.test(to) ? "_blank" : undefined} rel="noreferrer" className={cls} data-testid={testId}>{children}</a>;
  return <Link to={to} className={cls} data-testid={testId}>{children}</Link>;
};

export function ContactSplit({ b }: any) {
  const wa = waLink(useSite().site?.whatsapp);
  const id = sid(b.heading);
  const rows = [
    ["phone", b.phone_title || "By Phone", [[b.phone_label ?? "Toll Free", b.phone, tel(b.phone)], [b.phone_intl_label ?? "International", b.phone_intl, tel(b.phone_intl)]]],
    ["chat", b.chat_title || "Live Chat", [["", b.chat_label || "Connect", b.chat_url || wa || (b.email && `mailto:${b.email}`)]]],
    ["email", b.email_title || "Email", [["", b.email_label || "Connect", b.email && `mailto:${b.email}`]]],
    ["booking", b.booking_title || "By Appointment", [["", b.booking_label || "Book Now", b.booking_url]]],
    ["store", b.store_title, [["", b.store_label || "Visit Our Store", b.store_url]]],
  ].map(([k, t, lines]: any) => ({ k, t, lines: lines.filter(([, v, u]: any) => v && u) })).filter((r) => r.t && r.lines.length);
  return (
    <section id={id} className={`mx-auto px-5 md:px-12 py-8 md:py-12 grid gap-8 md:gap-16 items-center scroll-mt-48 ${b.image ? "max-w-[1200px] md:grid-cols-2" : "max-w-2xl"}`} data-testid={`contact-split-${id}`}>
      {b.image && <div className={b.reverse ? "md:order-2" : ""}><img src={b.image} alt={b.heading || ""} loading="lazy" className="w-full aspect-[6/5] object-cover" /></div>}
      <div className="text-center max-w-md mx-auto">
        <h2 className="font-display uppercase tracking-[0.08em] text-2xl md:text-3xl leading-snug">{b.heading}</h2>
        <div className="mt-7 space-y-5 text-[13px] md:text-sm">
          {rows.map((r) => (
            <div key={r.k} data-testid={`contact-row-${r.k}`}>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em]">{r.t}</p>
              {r.k === "store" && b.store_address && <p className="mt-1" data-testid="contact-store-address">{b.store_address}</p>}
              {r.lines.map(([l, v, u]: any) => <p key={v} className="mt-1">{l && `${l}: `}<Go to={u} testId={`contact-link-${r.k}-${sid(l || v)}`}>{v}</Go></p>)}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function IconRow({ b }: any) {
  const items = (b.items || []).filter((t: any) => t.label);
  return (
    <section className="bg-[var(--c-muted)] mt-12" data-testid="icon-row">
      <div className="max-w-[1200px] mx-auto px-5 md:px-12 py-16 md:py-20 text-center">
        {b.heading && <h2 className="font-display uppercase tracking-[0.08em] text-2xl md:text-3xl">{b.heading}</h2>}
        {b.text && <p className="mt-5 max-w-xl mx-auto text-[13px] md:text-sm leading-relaxed">{b.text}</p>}
        <div className={`mt-12 grid grid-cols-2 gap-y-10 gap-x-6 ${items.length >= 4 ? "md:grid-cols-4" : "md:grid-cols-3"}`}>
          {items.map((t: any, i: number) => {
            const Icon = ROW_ICONS[t.icon] || Gem;
            const inner = <><Icon size={56} strokeWidth={0.8} className="transition-transform duration-500 group-hover:-translate-y-1" /><span className="mt-5 text-[11px] tracking-[0.16em] uppercase">{t.label}</span></>;
            const cls = "group flex flex-col items-center transition-colors hover:text-[var(--c-primary)]";
            return t.url ? <Link key={i} to={t.url} className={cls} data-testid={`icon-row-${sid(t.label)}`}>{inner}</Link> : <div key={i} className={cls} data-testid={`icon-row-${sid(t.label)}`}>{inner}</div>;
          })}
        </div>
      </div>
    </section>
  );
}
